import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Gauge, Plus, X } from 'lucide-react';
import { equipmentApi, type Equipment } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Props {
  equipment: Pick<Equipment, 'id' | 'current_reading' | 'meter_unit'>;
}

/**
 * Hour-meter / cycle-counter panel.
 *
 * Visible only to tenants with Pro access (`useAuth().hasPro`).
 * Shows the equipment's current reading + recent log entries, and lets
 * any authenticated role log a new reading. The backend gates the
 * write endpoint with requireTier('pro'), so this is defense-in-depth UI.
 */
export function MeterPanel({ equipment }: Props) {
  const { hasPro } = useAuth();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [value, setValue]       = useState('');
  const [note, setNote]         = useState('');
  const [error, setError]       = useState('');

  const readingsQuery = useQuery({
    queryKey: ['equipment', equipment.id, 'meter-readings'],
    queryFn:  () => equipmentApi.listMeterReadings(equipment.id, 1, 5),
    enabled:  hasPro,
  });

  const logMutation = useMutation({
    mutationFn: () => equipmentApi.logMeterReading(equipment.id, {
      value: parseFloat(value),
      note:  note || undefined,
    }),
    onSuccess: () => {
      setValue('');
      setNote('');
      setError('');
      setFormOpen(false);
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id] });
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id, 'meter-readings'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to log reading';
      setError(msg);
    },
  });

  if (!hasPro) return null;

  const unit = equipment.meter_unit || 'units';
  const current = equipment.current_reading;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const num = parseFloat(value);
    if (!isFinite(num) || num < 0) {
      setError('Enter a valid non-negative number');
      return;
    }
    if (current !== null && current !== undefined && num < Number(current)) {
      setError(`Reading must be ≥ current value (${current}). Rollovers require admin.`);
      return;
    }
    logMutation.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="Meter Readings"
        action={!formOpen && (
          <Button size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="w-4 h-4" />
            Log Reading
          </Button>
        )}
      />
      <CardBody>
        <div className="flex items-center gap-3 mb-4">
          <Gauge className="w-8 h-8 text-brand-600" />
          <div>
            <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Current Reading</p>
            <p className="text-2xl font-semibold font-mono tracking-tight text-slate-900">
              {current !== null && current !== undefined ? `${current} ${unit}` : '—'}
            </p>
          </div>
        </div>

        {formOpen && (
          <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-900">New Meter Reading</h4>
              <button
                type="button"
                onClick={() => { setFormOpen(false); setError(''); }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Value ({unit})</label>
              <input
                type="number"
                step="any"
                min="0"
                value={value}
                onChange={e => setValue(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder={current ? `current: ${current}` : 'e.g. 1250'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={500}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g. after oil change"
              />
            </div>
            {error && <p className="text-red-600 text-xs">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setFormOpen(false); setError(''); }}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={logMutation.isPending}>
                {logMutation.isPending ? 'Logging…' : 'Log Reading'}
              </Button>
            </div>
          </form>
        )}

        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Recent Readings</h4>
          {readingsQuery.isLoading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : readingsQuery.data?.data.length === 0 ? (
            <p className="text-sm text-slate-400">No readings yet.</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {readingsQuery.data?.data.map(r => (
                <li key={r.id} className="py-2 flex items-baseline justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <span className="font-mono font-medium text-slate-900">{r.value} {unit}</span>
                    {r.note && <span className="ml-2 text-slate-500 truncate">— {r.note}</span>}
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {new Date(r.recorded_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
